import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Video, Loader2, CheckCircle2, XCircle, Circle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiBase';
import { TRUST_VERIFICATION_DELTAS } from '@swipeopinion/shared';

const MAX_DURATION = 30;

type Step = 'instructions' | 'recording' | 'review' | 'preparing' | 'submitting' | 'result';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Captures a frame from a recorded video blob at 1 second in.
 * Returns base64 PNG data (without the data URL prefix).
 */
function captureFrameFromBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(blob);
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;

    video.addEventListener('canplay', () => {
      video.currentTime = Math.min(1, video.duration * 0.5);
    }, { once: true });

    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      const maxWidth = 800;
      const scale = Math.min(1, maxWidth / (video.videoWidth || 640));
      canvas.width = (video.videoWidth || 640) * scale;
      canvas.height = (video.videoHeight || 480) * scale;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        const base64 = dataUrl.split(',')[1];
        URL.revokeObjectURL(objectUrl);
        resolve(base64);
      } else {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not get canvas context'));
      }
    }, { once: true });

    video.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load video for frame capture'));
    }, { once: true });

    video.load();
  });
}

export default function VideoVerificationModal({ isOpen, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('instructions');
  const [seconds, setSeconds] = useState(MAX_DURATION);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [result, setResult] = useState<{ passed: boolean; score: number; reason: string; attemptsRemaining?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      stopStream();
      clearInterval(timerRef.current);
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  function stopStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        void videoRef.current.play();
      }
    } catch {
      setError('Camera access denied. Please allow camera access and try again.');
    }
  }, []);

  function startRecording() {
    if (!streamRef.current) return;

    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current, { mimeType: 'video/webm' });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setVideoBlob(blob);
      setVideoUrl(URL.createObjectURL(blob));
      setRecordingDuration(Math.round((Date.now() - startTimeRef.current) / 1000));
      stopStream();
      setStep('review');
    };

    recorder.start();
    startTimeRef.current = Date.now();
    setSeconds(MAX_DURATION);
    setStep('recording');

    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          recorder.stop();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function stopRecording() {
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
  }

  async function handleStartRecording() {
    setError(null);
    await startCamera();
    setTimeout(() => startRecording(), 500);
  }

  function handleReRecord() {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    setResult(null);
    setStep('instructions');
  }

  async function handleSubmit() {
    if (!videoBlob) return;
    setStep('preparing');

    let frameBase64: string;
    try {
      frameBase64 = await captureFrameFromBlob(videoBlob);

      if (frameBase64.length < 1000) {
        setResult({ passed: false, score: 0, reason: 'Could not capture video frame. Please try again.' });
        setStep('result');
        return;
      }
    } catch {
      setResult({ passed: false, score: 0, reason: 'Failed to capture video frame. Please try recording again.' });
      setStep('result');
      return;
    }

    setStep('submitting');

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const hasAudio = videoBlob.type.includes('audio') || recordingDuration > 0;

      const res = await fetch(`${API_BASE}/api/member/verify-video`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ frameBase64, duration: recordingDuration, hasAudio }),
      });

      const json = await res.json();
      setResult({ passed: json.passed, score: json.score ?? 0, reason: json.reason ?? '', attemptsRemaining: json.attemptsRemaining });
      setStep('result');

      if (json.passed) {
        setTimeout(() => onSuccess(), 2000);
      }
    } catch (err) {
      setResult({ passed: false, score: 0, reason: (err as Error).message });
      setStep('result');
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="bg-[#5B4FE8] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-200" />
            <h2 className="text-white font-bold text-sm">Video Verification</h2>
          </div>
          <button onClick={onClose} className="text-purple-200 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {step === 'instructions' && (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto">
                <Video className="w-8 h-8 text-[#5B4FE8]" />
              </div>
              <h3 className="text-lg font-bold text-[#111827]">Record a 30-second video</h3>
              <p className="text-sm text-[#6B7280]">
                Please look at the camera and say: "I am <em>[your name]</em> and I agree to Swipe Opinion's terms of service."
              </p>
              <p className="text-xs text-[#6B7280]">We'll use your camera and microphone. The video is analyzed for verification and not stored.</p>
              {error && <p className="text-sm text-[#EF4444]">{error}</p>}
              <button onClick={() => void handleStartRecording()} className="w-full py-3 rounded-full bg-[#5B4FE8] text-white font-medium hover:bg-[#4338CA] transition-colors">
                Start Recording
              </button>
            </div>
          )}

          {step === 'recording' && (
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                  <Circle className="w-2.5 h-2.5 fill-current animate-pulse" /> REC
                </div>
                <div className="absolute top-3 right-3 bg-black/60 text-white text-sm font-bold px-3 py-1 rounded-full tabular-nums">
                  {seconds}s
                </div>
              </div>
              <button onClick={stopRecording} className="w-full py-3 rounded-full border-2 border-[#EF4444] text-[#EF4444] font-medium hover:bg-red-50 transition-colors">
                Stop Recording
              </button>
            </div>
          )}

          {step === 'review' && videoUrl && (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden bg-black aspect-video">
                <video src={videoUrl} className="w-full h-full object-cover" controls />
              </div>
              <div className="flex gap-3">
                <button onClick={handleReRecord} className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-[#6B7280] font-medium hover:bg-gray-50">
                  Re-record
                </button>
                <button onClick={() => void handleSubmit()} className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-white font-medium hover:bg-[#4338CA] transition-colors">
                  Submit for Review
                </button>
              </div>
            </div>
          )}

          {step === 'preparing' && (
            <div className="text-center space-y-3 py-8">
              <Loader2 className="w-10 h-10 text-[#5B4FE8] mx-auto animate-spin" />
              <p className="text-sm text-[#6B7280]">Preparing your video...</p>
            </div>
          )}

          {step === 'submitting' && (
            <div className="text-center space-y-3 py-8">
              <Loader2 className="w-10 h-10 text-[#5B4FE8] mx-auto animate-spin" />
              <p className="text-sm text-[#6B7280]">Analyzing your video...</p>
            </div>
          )}

          {step === 'result' && result && (
            <div className="text-center space-y-4 py-4">
              {result.passed ? (
                <>
                  <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto" />
                  <h3 className="text-lg font-bold text-[#111827]">Video verified!</h3>
                  <p className="text-sm text-[#10B981] font-medium">+{TRUST_VERIFICATION_DELTAS.VIDEO_VERIFIED} trust score added</p>
                </>
              ) : (
                <>
                  <XCircle className="w-14 h-14 text-[#EF4444] mx-auto" />
                  <h3 className="text-lg font-bold text-[#111827]">Verification failed</h3>
                  <p className="text-sm text-[#6B7280]">{result.reason}</p>
                  {(result.attemptsRemaining ?? 0) > 0 ? (
                    <button onClick={handleReRecord} className="w-full py-3 rounded-full bg-[#5B4FE8] text-white font-medium hover:bg-[#4338CA] transition-colors">
                      Try Again ({result.attemptsRemaining} left)
                    </button>
                  ) : (
                    <a
                      href="mailto:support@swipeopinion.com?subject=Video%20Verification%20Help"
                      className="w-full inline-block py-3 rounded-full border-2 border-[#E5E7EB] text-sm font-medium text-[#6B7280] hover:bg-gray-50 text-center"
                    >
                      Contact Support
                    </a>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
